
export enum Gender {
  Male = '男',
  Female = '女'
}

export enum District {
  North = '北區',
  Central = '中區',
  CentralSouth = '中南區',
  South = '南區',
  West = '西區',
  NorthEast = '東北區',
  SouthEast = '東南區',
  Foreign = '國外地區'
}

export enum EventStatus {
  Confirmed = 'confirmed',
  Pending = 'pending',
  NotArranged = 'not_arranged'
}

export enum LocationType {
  Clinic = 'clinic',
  Accommodation = 'accommodation',
  Church = 'church',
  Other = 'other'
}

export enum TimeOfDay {
  Morning = '上午',
  Afternoon = '下午',
  AllDay = '全天',
  Overnight = '住宿'
}

export interface ScheduleItem {
  date: string; // e.g., "11/14" or "11/14（五）"
  timeOfDay: TimeOfDay;
  location: string; // Location name
  locationType: LocationType;
  address?: string; // Full address for Google Maps
  activity: string; // e.g., "義診", "住宿", "福音活動"
  notes?: string; // Additional notes
}

export type EventType = 'mission' | 'assembly';

export interface EventOption {
  id: string;
  year: number;
  type?: EventType;
  dateRange: string;
  startDate?: string;
  endDate?: string;
  location: string;
  deadline: string;
  deadlineDate?: string;
  cancelDeadline?: string;      // 線上取消截止日（顯示用，如 09月16日）
  cancelDeadlineDate?: string;  // 線上取消截止日 ISO；未設定時預設為出隊前一個月
  status?: EventStatus;
  schedule?: ScheduleItem[];
  description?: string;
  capacity?: number;
  registrationClosed?: boolean;
  customTaskRoles?: string[];
}

export interface EventAssignment {
  leadershipRole?: string; // e.g., 隊長, 護理長
  taskRole?: string;       // e.g., 櫃台, 藥局
  taskSubDetail?: string;  // e.g., 西醫, 牙科, 自訂站點（跟診/流動目標站點）
  assistTarget?: string;   // 跟診或牙助所協助的醫師/牙醫姓名
  note?: string;
}

export interface Registrant {
  id: string; // Unique ID for system
  email: string;
  name: string;
  gender: Gender;
  church: string;
  district: District;
  phone: string;
  message: string;
  role: string; // Primary role (first selected role for backward compatibility)
  roles?: string[]; // All selected roles (multi-select support)
  otherRoleDescription?: string;
  studentSpecialization?: string; // 學生專業科目（醫學生、藥學生、護理學生等）
  westernMedicineSpecialty?: string; // 西醫次專科（骨科、小兒科等）
  therapistSpecialty?: string; // 治療師次分類（物理治療師、職能治療師等）
  canProvideOfficialSupport?: boolean; // 專業醫療人員報備支援狀態（true=可報備, false=無報備, undefined=不適用）
  officialSupportAvailability?: Record<string, string[]>; // 各梯隊可報備日期，key = eventId，value = ISO 日期陣列
  selectedEvents: string[]; // Array of Event IDs
  submissionDate: string;
  // Map eventId to specific assignments for that batch
  assignments?: Record<string, EventAssignment>;
  // Admin override for eligibility
  isVerifiedOverride?: boolean;
  // Admin approval for public visibility
  isApproved?: boolean;
  verificationResult?: VerificationResult;
  // 來源：'form' = 一般表單報名（預設）；'manual' = 後台手動新增的現場人員
  source?: 'form' | 'manual';
}

export interface HistoricalRecord {
  id: string;
  name: string;
  church: string; // Used for secondary verification
  yearAttended: number; // Year of mobilization meeting. 0 means attended but year unknown.
  // 補充的教會 key（scripts/enrich-record-churches.mjs）；有此欄時「第一次參與」比對優先使用，
  // [] 代表 church 欄位不可用（郵遞區號、誤植姓名等）。原 church 欄位不變。
  churches?: string[];
  // 由總名冊補充的手機／email 雜湊（utils/contactHash.ts，scripts/enrich-record-contacts.mjs）；
  // 只在同名但教會衝突時用來分辨轉會與同名不同人。無此欄 = 聯絡資料不詳。
  contactHashes?: string[];
}

// 過往出隊名單（trip_participants 集合，管理員專用）
export interface TripParticipant {
  id: string;
  name: string;
  years: number[];   // 出隊年份（西元）
  tripCount: number; // 出隊次數
  source: string;    // 匯入來源
  churches?: string[]; // 由總名冊補充的教會 key（scripts/enrich-record-churches.mjs）；無此欄 = 教會不詳
  contactHashes?: string[]; // 由總名冊補充的手機／email 雜湊（scripts/enrich-record-contacts.mjs）；無此欄 = 聯絡資料不詳
}

export interface VerificationResult {
  isEligible: boolean;
  matches: HistoricalRecord[];
  matchType: 'EXACT' | 'NAME_ONLY' | 'NONE' | 'WATCHLIST';
}

export interface WatchlistItem {
  id: string;
  name: string;
  church: string;
  addedDate: string; // ISO format
}

export type OutlookRecordStatus = 'draft' | 'review' | 'published';

export interface OutlookDayStat {
  id: string;
  label: string;
  patientCount: number;
  visitCount: number;
}

export interface OutlookDepartmentStat {
  id: string;
  name: string;
  visitCount: number;
}

export interface OutlookActionItem {
  id: string;
  issue: string;
  nextAction: string;
  owner: string;
  status: 'open' | 'done';
}

export type OutlookIssueStatus = 'open' | 'resolved' | 'dropped';

export interface OutlookIssueSource {
  recordId: string;
  eventLabel: string;
  groupName: string;
  itemId: string;
}

export interface OutlookIssueItem {
  id: string;
  issue: string;
  impact?: string;
  cause?: string;
  improvementMethod: string;
  owner: string;
  carryForward: boolean;
  status: OutlookIssueStatus;
  source?: OutlookIssueSource;
}

export interface OutlookReportGroup {
  id: string;
  name: string;
  order: number;
  isActive: boolean;
  currentSummary: string;
  retainedPractices?: string;
  supplementalNotes?: string;
  items: OutlookIssueItem[];
}

export interface OutlookMeetingFeedback {
  id: string;
  category: string;
  speaker: string;
  summary: string;
  followUp: string;
  owner: string;
  order: number;
}

export interface OutlookStatsSheetCandidate {
  spreadsheetId: string;
  title: string;
  url: string;
  modifiedTime: string;
  score: number;
  matchedReasons: string[];
}

export interface OutlookStatsSheetSource {
  spreadsheetId: string;
  title: string;
  url: string;
  modifiedTime: string;
  boundAt?: string;
  boundBy?: string;
}

export interface OutlookStatsSyncAudit {
  syncedAt: string;
  syncedBy: string;
  sourceModifiedTime: string;
  forcedIncomplete: boolean;
  warning?: string;
}

export interface OutlookDistributionRow {
  label: string;
  values: Record<string, number>;
  total?: number;
}

export interface OutlookStatsBreakdown {
  label: string;
  value: number;
}

export interface OutlookSyncedStats {
  totalPatients: number;
  totalVisits: number;
  dayStats: OutlookDayStat[];
  departmentStats: OutlookDepartmentStat[];
  genderRatio: string;
  ageRange: string;
  ageDistribution: OutlookDistributionRow[];
  faithDistribution: OutlookStatsBreakdown[];
  willingVisitFaithDistribution: OutlookStatsBreakdown[];
  unwillingVisitFaithDistribution: OutlookStatsBreakdown[];
  unknownVisitFaithDistribution: OutlookStatsBreakdown[];
  referralSourceDistribution: OutlookStatsBreakdown[];
}

export interface OutlookMeetingRecord {
  id: string;
  eventId: string;
  year: number;
  eventDateRange: string;
  eventLocation: string;
  title: string;
  meetingDateTime: string;
  meetingLocation: string;
  recorderName: string;
  recorderEmails?: string[];
  infoTeamName: string;
  status: OutlookRecordStatus;
  publicSummary: string;
  groupReports: string;
  reportGroups: OutlookReportGroup[];
  meetingFeedbacks?: OutlookMeetingFeedback[];
  gospelFollowUp: string;
  gratitudeNotes: string;
  dayStats: OutlookDayStat[];
  departmentStats: OutlookDepartmentStat[];
  genderRatio: string;
  ageRange: string;
  faithDistribution: string;
  referralSourceDistribution: string;
  actionItems: OutlookActionItem[];
  statsSheetSource?: OutlookStatsSheetSource;
  statsSync?: OutlookStatsSyncAudit;
  syncedStats?: OutlookSyncedStats;
  createdAt?: string;
  updatedAt?: string;
  submittedAt?: string;
  submittedBy?: string;
  lastEditedBy?: string;
  publishedAt?: string;
  reviewedBy?: string;
}

export type InventoryRequestStatus = 'submitted' | 'handled' | 'cancelled';

export interface InventoryItem {
  id: string;
  eventId?: string;
  department: string;
  name: string;
  quantity: number;
  unit: string;
  expiryDate?: string;
  note?: string;
  eventLabel?: string;
  isDeleted?: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ReplenishmentRequestLine {
  id: string;
  inventoryItemId?: string;
  inventoryItemIds?: string[];
  name: string;
  currentQuantity: number;
  requestedQuantity?: number;
  unit: string;
  expiryDate?: string;
  expiryBreakdown?: string;
  minimumQuantity?: number;
  targetQuantity?: number;
  autoReason?: string;
  note?: string;
  quantityEditedBy?: string;
  quantityEditedAt?: string;
}

export interface ReplenishmentRequest {
  id: string;
  department: string;
  status: InventoryRequestStatus;
  items: ReplenishmentRequestLine[];
  requestedBy: string;
  requestedAt: string;
  source?: 'manual' | 'auto';
  autoReason?: string;
  autoRuleSummary?: string;
  eventId?: string;
  eventLabel?: string;
  handledBy?: string;
  handledAt?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  quantityEditedBy?: string;
  quantityEditedAt?: string;
  note?: string;
}

export type InventoryLogAction = 'create' | 'update' | 'delete' | 'replenishment_request' | 'request_status' | 'request_update';

export interface InventoryLog {
  id: string;
  department: string;
  itemId?: string;
  itemName?: string;
  requestId?: string;
  action: InventoryLogAction;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  eventId?: string;
  eventLabel?: string;
  createdBy: string;
  createdAt: string;
}

/** 物資管理的梯次下拉選項，時間為毫秒；無法解析日期的梯次為 null。 */
export interface InventoryEventOption {
  id: string;
  label: string;
  startTime: number | null;
  endTime: number | null;
}

/** 一梯一組的現場清點通行碼，存在 settings/inventory_codes，只有管理員讀得到。 */
export interface InventoryAccessCode {
  code: string;
  eventLabel?: string;
  active?: boolean;
  expiresAt?: string | null;
  updatedAt?: string;
  updatedBy?: string;
}

export type InventoryAccessCodeMap = Record<string, InventoryAccessCode>;

/** 用通行碼換到的臨時身分，只能操作 eventId 這一梯的物資。 */
export interface InventoryCodeSession {
  eventId: string;
  eventLabel: string;
  /** 毫秒時間戳，過期後 Firestore 規則就不再放行。 */
  expiresAt: number;
  name: string;
}
